/**
 * Client-side image processing for upload endpoints.
 *
 * Goal: take a raw file from a phone's camera (often 4-8 MB, sometimes HEIC)
 * and produce a small JPEG data-URL (~80-150 KB) that any browser can render
 * as <img src>. This bypasses HEIC display bugs on Safari and removes the
 * slow-network upload pain for the public join form.
 *
 * Output: 800×800 max (longest-edge constraint), JPEG @ 0.85 quality.
 */

const MAX_DIMENSION = 800;
const JPEG_QUALITY = 0.85;

/**
 * Read a File into an HTMLImageElement (via blob URL).
 * Throws if the browser can't decode (most common cause: HEIC on Safari < 17
 * or Chrome on Android — both fail with an error event from <img>).
 */
const fileToImage = (file) =>
  new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("DECODE_FAILED"));
    };
    img.src = url;
  });

/**
 * Resize a File to a JPEG data-URL ≤ 800px on the longest edge.
 *
 * @param {File} file Original file from <input type="file">
 * @returns {Promise<{dataUrl: string, sizeBytes: number, width: number, height: number}>}
 *
 * @throws Error "DECODE_FAILED" — browser can't render the file (HEIC, corrupt, etc.)
 * @throws Error "CANVAS_FAILED" — canvas/toDataURL refused (very rare)
 */
export const resizeImageFile = async (file) => {
  const img = await fileToImage(file);

  // Compute new dims preserving aspect ratio
  let { width, height } = img;
  const longest = Math.max(width, height);
  if (longest > MAX_DIMENSION) {
    const ratio = MAX_DIMENSION / longest;
    width = Math.round(width * ratio);
    height = Math.round(height * ratio);
  }

  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("CANVAS_FAILED");
  // White background prevents PNG transparency turning black in JPEG.
  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, 0, width, height);
  ctx.drawImage(img, 0, 0, width, height);

  let dataUrl;
  try {
    dataUrl = canvas.toDataURL("image/jpeg", JPEG_QUALITY);
  } catch (e) {
    throw new Error("CANVAS_FAILED");
  }
  // Approx byte size: base64 expands by ~4/3
  const b64 = dataUrl.split(",")[1] || "";
  const sizeBytes = Math.round((b64.length * 3) / 4);
  return { dataUrl, sizeBytes, width, height };
};

/** Human-friendly error message for the toasts. */
export const imageErrorToast = (err) => {
  const msg = err?.message || "";
  if (msg === "DECODE_FAILED") {
    return "We couldn't read this image. Please try a JPG or PNG photo instead.";
  }
  if (msg === "CANVAS_FAILED") {
    return "Your browser couldn't process this photo. Try a smaller image.";
  }
  return "Could not load this photo. Try another image.";
};
